import { BrowserBackupService, sha256Blob } from './backup.js';
import type { IndexedDbSalonStore } from './indexeddb-store.js';

const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const PRODUCT_MARKER = 'calcomate-salon-browser';

interface GoogleTokenResponse { access_token?: string; expires_in?: number; error?: string; error_description?: string; }
interface GoogleTokenClient { requestAccessToken(options?: { prompt?: string }): void; }
interface DriveFile { id: string; name: string; createdTime: string; size?: string; appProperties?: Record<string,string>; }

declare global {
  interface Window {
    google?: { accounts: { oauth2: { initTokenClient(config: { client_id: string; scope: string; callback(response: GoogleTokenResponse): void; error_callback?(error: unknown): void }): GoogleTokenClient; revoke(token: string, callback: () => void): void; } } };
  }
}

export class GoogleDriveBackupAdapter {
  private accessToken: string | null = null;
  private expiresAt = 0;
  private clientId: string | null = null;

  constructor(private readonly store: IndexedDbSalonStore, private readonly backups: BrowserBackupService) {}

  async configure(clientId: string): Promise<void> {
    const value = clientId.trim();
    if (!/^\d+-[a-z0-9_-]+\.apps\.googleusercontent\.com$/i.test(value)) throw new Error('DRIVE_CLIENT_ID_INVALID');
    this.clientId = value; await this.store.setMeta('drive.clientId', value);
  }

  async configuredClientId(): Promise<string> { return this.clientId ?? await this.store.getMeta<string>('drive.clientId') ?? ''; }
  get connected(): boolean { return Boolean(this.accessToken && Date.now() < this.expiresAt); }

  async connect(clientId?: string): Promise<void> {
    if (clientId) await this.configure(clientId);
    this.clientId = await this.configuredClientId();
    if (!this.clientId) throw new Error('DRIVE_CLIENT_ID_REQUIRED');
    await this.loadIdentityLibrary();
    await new Promise<void>((resolve, reject) => {
      const configured = window.google!.accounts.oauth2.initTokenClient({
        client_id: this.clientId!, scope: DRIVE_SCOPE,
        callback: (response) => {
          if (response.error || !response.access_token) { reject(new Error(response.error_description ?? response.error ?? 'DRIVE_AUTH_FAILED')); return; }
          this.accessToken = response.access_token; this.expiresAt = Date.now() + Math.max(60, Number(response.expires_in ?? 3600) - 60) * 1000; resolve();
        }, error_callback: () => reject(new Error('DRIVE_AUTH_CANCELLED'))
      });
      configured.requestAccessToken({ prompt: '' });
    });
  }

  async disconnect(): Promise<void> {
    const token = this.accessToken; this.accessToken = null; this.expiresAt = 0;
    if (token && window.google) await new Promise<void>((resolve) => window.google!.accounts.oauth2.revoke(token, resolve));
  }

  async backupNow(password: string): Promise<DriveFile> {
    const token = this.requireToken(), encrypted = await this.backups.createEncryptedBlob(password), createdAt = new Date().toISOString();
    const metadata = { name: encrypted.fileName, mimeType: encrypted.blob.type, appProperties: { calcomateProduct: PRODUCT_MARKER, sha256: encrypted.sha256, formatVersion: '1', createdAt } };
    const session = await this.authorizedFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=resumable&fields=id,name,createdTime,size,appProperties', {
      method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json; charset=UTF-8', 'X-Upload-Content-Type': encrypted.blob.type, 'X-Upload-Content-Length': String(encrypted.blob.size) }, body: JSON.stringify(metadata)
    });
    const uploadUrl = session.headers.get('Location'); if (!uploadUrl) throw new Error('DRIVE_UPLOAD_SESSION_MISSING');
    const uploaded = await this.authorizedFetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': encrypted.blob.type }, body: encrypted.blob });
    const file = await uploaded.json() as DriveFile;
    const downloaded = await this.downloadFile(file.id);
    if (await sha256Blob(downloaded) !== encrypted.sha256) throw new Error('DRIVE_REMOTE_INTEGRITY_FAILED');
    await this.store.setMeta('backup.lastDriveAt', createdAt); await this.store.setMeta('backup.lastDriveFileId', file.id);
    try { await this.applyRetention(30); await this.store.setMeta('backup.retentionError', null); }
    catch (error) { await this.store.setMeta('backup.retentionError', { code: error instanceof Error ? error.message : 'DRIVE_RETENTION_FAILED', at: new Date().toISOString() }); }
    return file;
  }

  async listBackups(): Promise<DriveFile[]> {
    const query = encodeURIComponent(`appProperties has { key='calcomateProduct' and value='${PRODUCT_MARKER}' } and trashed=false`);
    const response = await this.authorizedFetch(`https://www.googleapis.com/drive/v3/files?q=${query}&orderBy=createdTime desc&pageSize=100&fields=files(id,name,createdTime,size,appProperties)`, { headers: { Authorization: `Bearer ${this.requireToken()}` } });
    return ((await response.json()) as { files?: DriveFile[] }).files ?? [];
  }

  async restoreLatest(password: string): Promise<void> {
    const latest = (await this.listBackups())[0]; if (!latest) throw new Error('DRIVE_BACKUP_NOT_FOUND');
    const blob = await this.downloadFile(latest.id), expected = latest.appProperties?.sha256;
    if (!expected || await sha256Blob(blob) !== expected) throw new Error('DRIVE_REMOTE_INTEGRITY_FAILED');
    await this.backups.restore(blob, password);
  }

  private async downloadFile(id: string): Promise<Blob> {
    const response = await this.authorizedFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(id)}?alt=media`, { headers: { Authorization: `Bearer ${this.requireToken()}` } });
    return response.blob();
  }
  private async applyRetention(limit: number): Promise<void> {
    const files = await this.listBackups();
    for (const file of files.slice(limit)) await this.authorizedFetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(file.id)}`, { method: 'DELETE', headers: { Authorization: `Bearer ${this.requireToken()}` } });
  }
  private requireToken(): string { if (!this.accessToken) throw new Error('DRIVE_AUTH_REQUIRED'); if (Date.now() >= this.expiresAt) { this.accessToken = null; throw new Error('DRIVE_AUTH_EXPIRED'); } return this.accessToken; }
  private async authorizedFetch(url: string, init: RequestInit): Promise<Response> {
    if (!navigator.onLine) throw new Error('DRIVE_OFFLINE_PENDING');
    const response = await fetch(url, init);
    if (response.status === 401) { this.accessToken = null; throw new Error('DRIVE_AUTH_EXPIRED'); }
    if (response.status === 403) throw new Error('DRIVE_PERMISSION_OR_QUOTA');
    if (!response.ok) throw new Error(`DRIVE_REQUEST_FAILED_${response.status}`);
    return response;
  }
  private async loadIdentityLibrary(): Promise<void> {
    if (window.google?.accounts.oauth2) return;
    await new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity]');
      if (existing) { existing.addEventListener('load', () => resolve(), { once: true }); existing.addEventListener('error', () => reject(new Error('DRIVE_IDENTITY_LOAD_FAILED')), { once: true }); return; }
      const script = document.createElement('script'); script.src = 'https://accounts.google.com/gsi/client'; script.async = true; script.defer = true; script.dataset.googleIdentity = '1'; script.onload = () => resolve(); script.onerror = () => reject(new Error('DRIVE_IDENTITY_LOAD_FAILED')); document.head.append(script);
    });
  }
}
